import "./shell.css";
import { useState } from "react";
import {
  ClipboardCheck,
  ChartNoAxesColumn,
  ListTodo,
  LayoutDashboard,
  Home,
  Cable,
} from "lucide-react";
import { SearchInput } from "@hub/ui";
import { useBladeStack } from "./BladeStackProvider";
import { BladeCanvas } from "./BladeCanvas";
import {
  AuditTourProvider,
  useTourNavigator,
} from "../audit/help/TourProvider";
import { HelpMenu } from "../audit/help/HelpMenu";
import { Walkthrough } from "../audit/help/Walkthrough";
import { TourDemoData } from "../audit/help/TourDemoData";
import { Breadcrumb } from "./Breadcrumb";
import { Sidebar, type NavAnchor } from "./Sidebar";
import { useSignedInUser } from "./signedInUser";
import { registerViewPrimitive } from "./registry";
import { listPrimitive } from "./primitives/list";
import { recordPrimitive } from "./primitives/record";
import { formPrimitive } from "./primitives/form";
import { dashboardPrimitive } from "./primitives/dashboard";
import { documentPrimitive } from "./primitives/document";
import { searchPrimitive } from "./primitives/search";
import { auditBatchesPrimitive } from "../audit/primitives/auditBatches";
import { auditBatchPrimitive } from "../audit/primitives/auditBatch";
import { auditComparePrimitive } from "../audit/primitives/auditCompare";
import { auditDashboardPrimitive } from "../audit/primitives/auditDashboard";
import { auditCodeCasesPrimitive } from "../audit/primitives/auditCodeCases";
import { AUDIT_DEFAULT_STACK, AUDIT_METRICS_STACK } from "../audit/stack";
import type { BladeStackState } from "./types";

// The AR Workbench shell frame (HUB-275): sidebar + topbar + breadcrumb + blade
// canvas. Ported from the agreed mockup (light-only chrome). Wrap it in a
// BladeStackProvider; the seeded preview (ShellPreview) and the URL-synced route
// (WorkbenchShellRoute) both render this.

// View-primitive catalog (HUB-276): List (rows → drill), Record (entity detail),
// Form (claim-coding controls), Dashboard (KPIs + bars), Document (viewer frame).
registerViewPrimitive(listPrimitive);
registerViewPrimitive(recordPrimitive);
registerViewPrimitive(formPrimitive);
registerViewPrimitive(dashboardPrimitive);
registerViewPrimitive(documentPrimitive);
registerViewPrimitive(searchPrimitive);
// Audit workbench primitives (HUB-504): batch queue + batch detail + the
// read-only side-by-side compare frame (opened in focus mode).
registerViewPrimitive(auditBatchesPrimitive);
registerViewPrimitive(auditBatchPrimitive);
registerViewPrimitive(auditComparePrimitive);
// Program metrics: the agent-vs-coder scoreboard the whole study exists to produce.
registerViewPrimitive(auditDashboardPrimitive);
// The cases behind one report row (a code, or a reason) — CSV export lives here.
registerViewPrimitive(auditCodeCasesPrimitive);

/** Default queue → client → claim path (list → record → form). */
export const DEFAULT_STACK: BladeStackState = {
  blades: [
    {
      key: "list",
      title: "AR worklist",
      eyebrow: "Queue",
      entityId: "q1",
      width: "wide",
    },
    {
      key: "record",
      title: "Cascade Family Health",
      eyebrow: "Client",
      entityId: "c1",
      width: "med",
    },
    {
      key: "form",
      title: "CL-1023-4829",
      eyebrow: "Claim",
      entityId: "cl1",
      width: "wide",
    },
  ],
  activeIndex: 2,
  mode: "explorer",
};

/** Lucide "panel-left" — the sidebar collapse toggle (matches the mockup). */
function PanelIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M9 3v18" />
    </svg>
  );
}

/**
 * The walkthrough's navigator of LAST RESORT.
 *
 * A child component rather than a hook call in the shell itself, because the
 * shell RENDERS AuditTourProvider — a hook there sits above the context, reads
 * null, and registers nothing. Silently: useAuditTour returns null by design so
 * the audit views can mount without a provider, so the no-op looks exactly like
 * a working registration.
 *
 * It sits outermost in the chain, so the mounted audit views get first refusal
 * and only what none of them can reach arrives here. That is the dashboard,
 * which is a sibling STACK rather than a blade any view can open — those steps
 * reached nobody at all, and the tour's closing three narrated a screen it never
 * showed. It answers "queue" too, for a tour started from the metrics stack,
 * where the mounted view cannot open the queue either.
 */
function TourStackNavigator({
  auditActive,
  metricsActive,
  gotoStack,
}: {
  auditActive: boolean;
  metricsActive: boolean;
  gotoStack: (next: BladeStackState) => void;
}) {
  useTourNavigator((screen) => {
    if (screen === "queue") {
      if (!auditActive) gotoStack(AUDIT_DEFAULT_STACK);
      return true;
    }
    if (screen === "dashboard") {
      if (!metricsActive) gotoStack(AUDIT_METRICS_STACK);
      return true;
    }
    return false;
  });
  return null;
}

export function WorkbenchShell({
  enableAuditNav = false,
}: { enableAuditNav?: boolean } = {}) {
  const { state, dispatch } = useBladeStack();
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState("");
  // The signed-in principal, from the session. Never a literal — see
  // signedInUser.ts for what the hardcoded one cost.
  const user = useSignedInUser();

  // Swap the whole stack from a sidebar entry (HUB-504). Used by the audit-queue
  // entry to enter the audit workbench and by the coding-queue entry to return.
  const gotoStack = (next: BladeStackState) =>
    dispatch({ type: "hydrate", state: next });
  // Which landing stack is showing, keyed off the root blade. Derived rather
  // than negated: "My coding queue" used to be `!auditActive`, which lit it up
  // whenever ANY non-queue stack was open — including program metrics.
  const rootKey = state.blades[0]?.key;
  const auditActive = rootKey === "audit-batches";
  const metricsActive = rootKey === "audit-metrics";

  // Chrome that is not backed by anything in this deployment is DEV-ONLY
  // (HUB-509 pilot). Every case below has the same symptom: an affordance that
  // looks live and does nothing.
  //
  //   "My coding queue"    — hardcoded count of 23, opens DEFAULT_STACK, a demo
  //                          path seeded with literal ids ("Cascade Family
  //                          Health", "CL-1023-4829").
  //   "Team SQDIP board"   — no onSelect; clicking does nothing.
  //   "Home",
  //   "Connected systems"  — likewise no onSelect. They were the mockup's
  //                          standing Workspace anchors, and nothing was ever
  //                          built behind either.
  //   Global search        — the one that is NOT dead code. The client is fully
  //                          built and bound to SearchService (HUB-281/369),
  //                          but storage.search.enabled is unset in this
  //                          deployment, so initSearchService returns without
  //                          registering and its APIContributor handler is
  //                          never mounted. Every query resolves to "Search
  //                          failed. Try again." — which reads as a broken
  //                          product rather than an unshipped one.
  //
  // Shipping these to a reviewer or auditor invites them to trust a number
  // nobody computed, and makes the entries that DO work harder to find among
  // ones that look equally live.
  //
  // Gated on import.meta.env.DEV rather than deleted: they are the design's
  // intended surface and the preview harness still demonstrates them. Search in
  // particular needs no client work to come back — set storage.search.enabled,
  // confirm the RPC is mounted, then drop it out of this gate.
  const showDemoNav = import.meta.env.DEV;
  const demoPins: NavAnchor[] = showDemoNav
    ? [
        {
          label: "My coding queue",
          icon: ListTodo,
          count: 23,
          active: !auditActive && !metricsActive,
          onSelect: () => gotoStack(DEFAULT_STACK),
        },
        { label: "Team SQDIP board", icon: LayoutDashboard },
      ]
    : [];

  const anchors: NavAnchor[] = showDemoNav
    ? [
        { label: "Home", icon: Home },
        { label: "Connected systems", icon: Cable },
      ]
    : [];

  const pins: NavAnchor[] = enableAuditNav
    ? [
        {
          label: "Evaluation queue",
          icon: ClipboardCheck,
          active: auditActive,
          onSelect: () => gotoStack(AUDIT_DEFAULT_STACK),
        },
        {
          label: "Program metrics",
          icon: ChartNoAxesColumn,
          active: metricsActive,
          onSelect: () => gotoStack(AUDIT_METRICS_STACK),
        },
        ...demoPins,
      ]
    : demoPins;

  // Global search opens an EPHEMERAL results blade — ephemeral so the codec keeps
  // the (potentially PHI) query out of the URL (HUB-351 F3). Appended at the end
  // so it doesn't truncate the current path; the user closes it when done.
  const runSearch = (raw: string) => {
    const q = raw.trim();
    if (!q) return;
    dispatch({
      type: "open",
      at: Math.max(state.blades.length - 1, -1),
      blade: {
        key: "search",
        title: `Search: ${q}`,
        eyebrow: "Search",
        ephemeral: true,
        width: "wide",
        params: { q },
      },
    });
  };

  return (
    // The tour provider wraps the whole shell so the Help control in the top
    // bar and the overlay over the canvas share one state, and so a step that
    // navigates between views does not unmount the tour driving it.
    <AuditTourProvider>
      <TourStackNavigator
        auditActive={auditActive}
        metricsActive={metricsActive}
        gotoStack={gotoStack}
      />
      <div className="hub-shell">
        <Sidebar
          collapsed={collapsed}
          anchors={anchors}
          pins={pins}
          user={user}
        />
        <div className="main">
          {/* Single top bar (HUB-504): [sidebar toggle] [breadcrumb trail, left] …
            [mode toggle] [search, right]. The breadcrumb rail and the top bar were
            two separate rows; they're collapsed into one here. The breadcrumb trail
            truncates gracefully when long; the search keeps a sensible min-width. */}
          <header className="topbar">
            <button
              type="button"
              className="icon-btn"
              title="Toggle sidebar"
              aria-label="Toggle sidebar"
              aria-pressed={collapsed}
              onClick={() => setCollapsed((c) => !c)}
              style={{ flexShrink: 0 }}
            >
              <PanelIcon />
            </button>
            <div className="topbar-crumbs">
              <Breadcrumb />
            </div>
            {/* The audit flow is focus-mode-first (a full-screen single-view app), so
              its full-bleed drill-stack owns the presentation — the explorer/focus
              toggle is hidden while the audit stack is active to keep two xwide
              blades from ever fanning out into an overflowing multi-column canvas. */}
            {!auditActive && (
              <div className="modeseg" title="Presentation mode">
                <button
                  type="button"
                  className={state.mode === "explorer" ? "on" : ""}
                  onClick={() =>
                    dispatch({ type: "setMode", mode: "explorer" })
                  }
                >
                  Explorer
                </button>
                <button
                  type="button"
                  className={state.mode === "focus" ? "on" : ""}
                  onClick={() => dispatch({ type: "setMode", mode: "focus" })}
                >
                  Focus
                </button>
              </div>
            )}
            {/* Help & guided tour. Sits with the global controls rather than
              inside a view, because the walkthrough crosses screens — anchoring
              it to one view would make it vanish exactly when a lost reviewer
              navigated away looking for it. */}
            <HelpMenu />
            {showDemoNav && (
              <div className="gsearch">
                <SearchInput
                  value={query}
                  onChange={setQuery}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") runSearch(e.currentTarget.value);
                  }}
                  placeholder="Search clients, providers, patients, claims, codes…"
                />
              </div>
            )}
          </header>
          {/* The walkthrough needs something to point at. When the reviewer's
              own queue is empty — the usual state at first sign-in, which is
              exactly when the tour fires — the audit views below resolve
              against example data instead. Both conditions matter; see
              TourDemoData. */}
          <TourDemoData>
            <BladeCanvas />
          </TourDemoData>
          <Walkthrough />
        </div>
      </div>
    </AuditTourProvider>
  );
}
